<script lang="ts">
export default {
  name: 'AiAssistant'
}
</script>
<script lang="ts" setup>
import aiIcon from '@/assets/icons/ai-icon.svg'
import quickAiIcon from '@/assets/icons/ai图标.svg'
import { OUTER_USER_TYPE } from '@/views/UserAccounts/js/config'
import { BUSINESS_TYPE } from '@/common/constants'
import { useUserStore } from '@/store/modules/user'
import {
  chatAiStream,
  deleteAiConversation,
  getAiConversationMessages,
  listAiConversations,
  resolveAiConversation,
  AI_RESOLVE_STATUS
} from '@/api/ai-assistant'
import type { AiConversationDto, AiMessageDto } from '@/api/ai-assistant/model.d'
import { formatAiMessageHtml } from './page-links'
import { useMenu } from '@/hooks/useMenu'
import { ElMessage, ElMessageBox } from 'element-plus'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import quickCloseIcon from '@/assets/icons/ai-close.svg'
import quickSparkleIcon from '@/assets/icons/ai-sparkle.svg'
import quickChevronIcon from '@/assets/icons/danfangxiang.svg'
import headerTitleIcon from '@/assets/icons/zhinengti.svg'
import newChatIcon from '@/assets/icons/xinduihua.svg'
import clearHistoryIcon from '@/assets/icons/qingkong.svg'
import sendIconEnabled from '@/assets/icons/lansefasong.svg'
import sendIconDisabled from '@/assets/icons/huisefasong.svg'
import stopIcon from '@/assets/icons/stop.svg'
import solvedIcon from '@/assets/icons/lansedianzan.svg'
import unsolvedIcon from '@/assets/icons/huisedianzan 2.svg'

type MessageRole = 'assistant' | 'user'
type MessageKind = 'greeting' | 'reply' | 'pending' | 'stopped'

interface AiMessage {
  id: string
  role: MessageRole
  content: string
  kind?: MessageKind
  pending?: boolean
}

interface AiSession {
  id: string
  conversationId: string
  title: string
  updatedAt: number
  resolveStatus: number
  messages: AiMessage[]
}

const AI_GREETING =
  'HI，我是您的AI助手，负责平台首营、报价、结算全流程的支持。有问题随时问我，秒回解答!'
const QUICK_QUESTIONS = ['怎么确认订单和发货？', '对账可以按单对账吗？', '如何查询付款凭证']
const ENTRY_SIZE = 50
const ENTRY_EDGE_GAP = 8
const DEFAULT_ENTRY_BOTTOM = 44
const ENTRY_BOTTOM_STORAGE_KEY = 'ai-assistant-entry-bottom'
const ENTRY_DRAG_THRESHOLD = 4

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { checkPathAvailable } = useMenu()
const showChatPanel = ref(false)
const showQuickPanel = ref(false)
const draft = ref('')
const inputFocused = ref(false)
const assistantRootRef = ref<HTMLElement>()
const chatPanelRef = ref<HTMLElement>()
const scrollBodyRef = ref<HTMLElement>()
const hideQuickPanelTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const entryBottom = ref(DEFAULT_ENTRY_BOTTOM)
const isDragging = ref(false)
const suppressEntryClick = ref(false)
let dragStartY = 0
let dragStartBottom = 0
let dragMoved = false
let dragPointerId: number | null = null
let resetSuppressClickTimer: ReturnType<typeof setTimeout> | null = null
const activePendingMessageId = ref('')
const chatAbortController = ref<AbortController | null>(null)
const resolvingFeedback = ref(false)
const historyLoading = ref(false)
const historyLoadToken = ref(0)

const sessions = ref<AiSession[]>([])
const activeSessionId = ref('')

const routePrefixWhiteList = [
  '/index',
  '/enquiry-management',
  '/invoice-management',
  '/bill-management',
  '/payment-management',
  '/async-download',
  '/supply/record',
  '/supply/record-money-tree',
  '/supply/reject-record',
  '/supply/cash-cow-drug-test-report'
]

/** 登录灰度 + 成员类型（供应商 / 内部账号） + 合作业务类型（非仅YQS） */
const canShowByUser = computed(() => {
  const userInfo = userStore.getUserInfo
  if (!userInfo) return false
  // 内部账号直接显示
  if (userInfo.outerUserType === OUTER_USER_TYPE.LEYOKEY) return true
  // 供应商账号需要有非YQS的合作业务类型
  if (userInfo.outerUserType === OUTER_USER_TYPE.SRM) {
    const businessTypeList = userInfo.businessTypeList || []
    // 只要有一个业务类型不是YQS就展示
    return businessTypeList.some((type) => type !== BUSINESS_TYPE.CASH_COW)
  }
  return false
})

const canShowByRoute = computed(() => {
  const currentPath = route.path || ''
  return routePrefixWhiteList.some((prefix) => {
    if (!prefix) return false
    return currentPath === prefix || currentPath.startsWith(`${prefix}/`)
  })
})

const canShowAssistant = computed(() => canShowByUser.value && canShowByRoute.value)

const activeSession = computed(() => {
  return sessions.value.find((item) => item.id === activeSessionId.value)
})

const activeMessages = computed(() => {
  return activeSession.value?.messages ?? []
})

const lastReplyMessageId = computed(() => {
  const messages = activeMessages.value
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (message.role === 'assistant' && message.kind === 'reply' && !message.pending) {
      return message.id
    }
  }
  return ''
})

const sessionFeedback = computed(() => {
  const status = activeSession.value?.resolveStatus
  if (status === AI_RESOLVE_STATUS.SOLVED) return 'solved'
  if (status === AI_RESOLVE_STATUS.UNSOLVED) return 'unsolved'
  return null
})

const canSend = computed(() => {
  return !!draft.value.trim()
})

const isReplying = computed(() => {
  return !!activePendingMessageId.value
})

const sendButtonDisabled = computed(() => {
  return !isReplying.value && !canSend.value
})

const sendButtonIcon = computed(() => {
  if (isReplying.value) return stopIcon
  return canSend.value ? sendIconEnabled : sendIconDisabled
})

function handleMessageClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  const link = target?.closest?.('.ai-route-link') as HTMLElement | null
  if (!link) return
  event.preventDefault()
  const path = link.dataset.aiRoute
  if (!path) return
  if (!checkPathAvailable(path.split('#')[0])) {
    ElMessage.warning('暂无该页面访问权限')
    return
  }
  void router.push(path)
  closeChatPanel()
}

function createGreetingSession(): AiSession {
  const now = Date.now()
  return {
    id: `session-${now}`,
    conversationId: '',
    title: '新对话',
    updatedAt: now,
    resolveStatus: AI_RESOLVE_STATUS.NONE,
    messages: [
      {
        id: `assistant-${now}`,
        role: 'assistant',
        content: AI_GREETING,
        kind: 'greeting'
      }
    ]
  }
}

function resetToGreetingSession() {
  const defaultSession = createGreetingSession()
  sessions.value = [defaultSession]
  activeSessionId.value = defaultSession.id
}

function isGreetingOnlySession(session?: AiSession) {
  if (!session) return true
  if (session.conversationId) return false
  return session.messages.every((item) => item.kind === 'greeting')
}

function mapHistoryRole(role?: string): MessageRole {
  const normalized = (role || '').toLowerCase()
  if (normalized === 'user' || normalized === 'human') return 'user'
  return 'assistant'
}

function mapHistoryMessages(list: AiMessageDto[]): AiMessage[] {
  const now = Date.now()
  return (list || [])
    .filter((item) => !!item.content?.trim())
    .map((item, index) => {
      const role = mapHistoryRole(item.role)
      return {
        id: item.recordId || `history-${now}-${index}`,
        role,
        content: item.content || '',
        kind: role === 'assistant' ? 'reply' : undefined
      }
    })
}

function pickLatestConversation(records: AiConversationDto[]) {
  if (!records.length) return undefined
  return [...records].sort((a, b) => {
    const timeA = new Date(a.mdatetime || a.cdatetime || 0).getTime()
    const timeB = new Date(b.mdatetime || b.cdatetime || 0).getTime()
    return timeB - timeA
  })[0]
}

/** 打开对话框时拉取最近一次会话历史 */
async function loadLatestHistory() {
  if (isReplying.value) return
  const current = activeSession.value
  // 当前页已有进行中的会话内容，不覆盖
  if (current && !isGreetingOnlySession(current)) {
    scrollToBottom()
    return
  }

  const token = ++historyLoadToken.value
  historyLoading.value = true
  try {
    const page = await listAiConversations({ pageNo: 1, pageSize: 10 })
    if (token !== historyLoadToken.value) return

    const latest = pickLatestConversation(page?.records || [])
    if (!latest?.conversationId) return

    const messagesRes = await getAiConversationMessages({
      conversationId: latest.conversationId,
      limit: 100
    })
    if (token !== historyLoadToken.value) return
    // 拉取期间用户已开始新对话，不覆盖
    if (isReplying.value || !isGreetingOnlySession(activeSession.value)) return

    const historyMessages = mapHistoryMessages(messagesRes?.messageList || [])
    const now = Date.now()
    const session: AiSession = {
      id: `session-${latest.conversationId}`,
      conversationId: latest.conversationId,
      title: (latest.content || '历史对话').slice(0, 12),
      updatedAt: now,
      resolveStatus: latest.resolveStatus ?? AI_RESOLVE_STATUS.NONE,
      messages: [
        {
          id: `assistant-greeting-${now}`,
          role: 'assistant',
          content: AI_GREETING,
          kind: 'greeting'
        },
        ...historyMessages
      ]
    }
    sessions.value = [session]
    activeSessionId.value = session.id
    scrollToBottom()
  } catch (error) {
    // 打开时加载失败不打断使用，保留欢迎语
    console.error(error)
  } finally {
    if (token === historyLoadToken.value) {
      historyLoading.value = false
    }
  }
}

function scrollToBottom() {
  nextTick(() => {
    const body = scrollBodyRef.value
    if (!body) return
    body.scrollTop = body.scrollHeight
  })
}

function openChatPanel(options: { loadHistory?: boolean } = {}) {
  if (!canShowAssistant.value) return
  clearHideQuickPanelTimer()
  showQuickPanel.value = false
  showChatPanel.value = true
  if (options.loadHistory !== false) {
    void loadLatestHistory()
  }
  scrollToBottom()
}

function clampEntryBottom(bottom: number) {
  const maxBottom = Math.max(ENTRY_EDGE_GAP, window.innerHeight - ENTRY_SIZE - ENTRY_EDGE_GAP)
  return Math.min(Math.max(ENTRY_EDGE_GAP, bottom), maxBottom)
}

function loadSavedEntryBottom() {
  try {
    const saved = localStorage.getItem(ENTRY_BOTTOM_STORAGE_KEY)
    if (saved == null) return
    const value = Number(saved)
    if (Number.isFinite(value)) {
      entryBottom.value = clampEntryBottom(value)
    }
  } catch (error) {
    // 忽略本地存储异常，使用默认位置
  }
}

function persistEntryBottom() {
  try {
    localStorage.setItem(ENTRY_BOTTOM_STORAGE_KEY, String(entryBottom.value))
  } catch (error) {
    // 忽略本地存储异常
  }
}

function handleResize() {
  entryBottom.value = clampEntryBottom(entryBottom.value)
}

function clearResetSuppressClickTimer() {
  if (resetSuppressClickTimer) {
    clearTimeout(resetSuppressClickTimer)
    resetSuppressClickTimer = null
  }
}

function unbindEntryDragListeners() {
  window.removeEventListener('pointermove', handleEntryPointerMove)
  window.removeEventListener('pointerup', handleEntryPointerUp)
  window.removeEventListener('pointercancel', handleEntryPointerUp)
}

function handleEntryPointerDown(event: PointerEvent) {
  if (event.button !== 0) return
  dragStartY = event.clientY
  dragStartBottom = entryBottom.value
  dragMoved = false
  dragPointerId = event.pointerId
  const target = event.currentTarget as HTMLElement
  if (typeof target.setPointerCapture === 'function') {
    target.setPointerCapture(event.pointerId)
  }
  window.addEventListener('pointermove', handleEntryPointerMove)
  window.addEventListener('pointerup', handleEntryPointerUp)
  window.addEventListener('pointercancel', handleEntryPointerUp)
}

function handleEntryPointerMove(event: PointerEvent) {
  if (dragPointerId !== event.pointerId) return
  const deltaY = dragStartY - event.clientY
  if (!dragMoved && Math.abs(event.clientY - dragStartY) < ENTRY_DRAG_THRESHOLD) return
  dragMoved = true
  isDragging.value = true
  clearHideQuickPanelTimer()
  showQuickPanel.value = false
  entryBottom.value = clampEntryBottom(dragStartBottom + deltaY)
}

function handleEntryPointerUp(event: PointerEvent) {
  if (dragPointerId !== null && event.pointerId !== dragPointerId) return
  unbindEntryDragListeners()
  dragPointerId = null
  isDragging.value = false
  if (!dragMoved) return
  persistEntryBottom()
  suppressEntryClick.value = true
  clearResetSuppressClickTimer()
  resetSuppressClickTimer = setTimeout(() => {
    suppressEntryClick.value = false
    resetSuppressClickTimer = null
  }, 300)
}

function handleOpenChatPanel() {
  if (suppressEntryClick.value || isDragging.value) return
  openChatPanel()
}

function closeChatPanel() {
  clearHideQuickPanelTimer()
  showChatPanel.value = false
}

function clearHideQuickPanelTimer() {
  if (hideQuickPanelTimer.value) {
    clearTimeout(hideQuickPanelTimer.value)
    hideQuickPanelTimer.value = null
  }
}

function handleMouseEnterEntry() {
  clearHideQuickPanelTimer()
  if (showChatPanel.value || !canShowAssistant.value || isDragging.value) return
  showQuickPanel.value = true
}

function handleMouseLeaveEntry() {
  clearHideQuickPanelTimer()
  hideQuickPanelTimer.value = setTimeout(() => {
    showQuickPanel.value = false
    hideQuickPanelTimer.value = null
  }, 200)
}

function closeQuickPanel() {
  clearHideQuickPanelTimer()
  showQuickPanel.value = false
}

function ensureActiveSession() {
  if (activeSession.value) return activeSession.value
  const defaultSession = createGreetingSession()
  sessions.value = [defaultSession, ...sessions.value]
  activeSessionId.value = defaultSession.id
  return defaultSession
}

function abortChatStream() {
  if (chatAbortController.value) {
    chatAbortController.value.abort()
    chatAbortController.value = null
  }
}

async function setSessionFeedback(
  status: typeof AI_RESOLVE_STATUS.SOLVED | typeof AI_RESOLVE_STATUS.UNSOLVED
) {
  const session = activeSession.value
  if (!session?.conversationId || resolvingFeedback.value) return
  if (session.resolveStatus === status) return

  resolvingFeedback.value = true
  try {
    await resolveAiConversation({
      conversationId: session.conversationId,
      resolveStatus: status
    })
    session.resolveStatus = status
    session.updatedAt = Date.now()
  } catch (error) {
    ElMessage.error((error as Error)?.message || '提交反馈失败')
  } finally {
    resolvingFeedback.value = false
  }
}

function stopReply() {
  if (!activePendingMessageId.value) return
  const session = activeSession.value
  if (!session) return
  const pendingMessage = session.messages.find((item) => item.id === activePendingMessageId.value)
  abortChatStream()
  if (pendingMessage) {
    pendingMessage.pending = false
    pendingMessage.kind =
      pendingMessage.content && pendingMessage.content !== '分析中...' ? 'reply' : 'stopped'
    if (!pendingMessage.content || pendingMessage.content === '分析中...') {
      pendingMessage.content = '已停止回答'
      pendingMessage.kind = 'stopped'
    }
  }
  session.updatedAt = Date.now()
  activePendingMessageId.value = ''
  scrollToBottom()
}

function handleSendOrStop() {
  if (isReplying.value) {
    stopReply()
    return
  }
  void sendMessage()
}

function sendMessage(content = draft.value) {
  if (isReplying.value) return
  const text = content.trim()
  if (!text) return
  const session = ensureActiveSession()
  const now = Date.now()
  session.updatedAt = now
  if (session.title === '新对话') {
    session.title = text.slice(0, 12)
  }
  session.messages.push({
    id: `user-${now}`,
    role: 'user',
    content: text
  })
  const pendingMessage: AiMessage = {
    id: `assistant-pending-${now}`,
    role: 'assistant',
    content: '分析中...',
    pending: true,
    kind: 'pending'
  }
  session.messages.push(pendingMessage)
  activePendingMessageId.value = pendingMessage.id
  // 新一轮问答重置解决状态展示（实际状态以服务端为准，提交后会更新）
  session.resolveStatus = AI_RESOLVE_STATUS.NONE

  draft.value = ''
  openChatPanel()
  scrollToBottom()

  abortChatStream()
  chatAbortController.value = chatAiStream(
    {
      content: text,
      conversationId: session.conversationId || undefined
    },
    {
      onConversationId: (conversationId) => {
        const current = sessions.value.find((item) => item.id === session.id)
        if (!current) return
        current.conversationId = conversationId
      },
      onContent: (chunk) => {
        const current = sessions.value.find((item) => item.id === session.id)
        if (!current) return
        const target = current.messages.find((item) => item.id === pendingMessage.id)
        if (!target) return
        if (target.content === '分析中...' || target.kind === 'pending') {
          target.content = chunk
          target.kind = 'reply'
        } else {
          target.content += chunk
        }
        target.pending = true
        scrollToBottom()
      },
      onError: (message) => {
        const current = sessions.value.find((item) => item.id === session.id)
        if (!current) return
        const target = current.messages.find((item) => item.id === pendingMessage.id)
        if (target) {
          target.pending = false
          target.kind = 'stopped'
          target.content = message || '回答失败，请稍后重试'
        }
        activePendingMessageId.value = ''
        chatAbortController.value = null
        ElMessage.error(message || 'AI服务异常')
        scrollToBottom()
      },
      onDone: () => {
        const current = sessions.value.find((item) => item.id === session.id)
        if (current) {
          const target = current.messages.find((item) => item.id === pendingMessage.id)
          if (target) {
            target.pending = false
            if (target.content === '分析中...') {
              target.content = '暂无回复'
              target.kind = 'stopped'
            } else if (target.kind !== 'stopped') {
              target.kind = 'reply'
            }
          }
          current.updatedAt = Date.now()
        }
        activePendingMessageId.value = ''
        chatAbortController.value = null
        scrollToBottom()
      }
    }
  )
}

function sendFromQuickQuestion(question: string) {
  // 直接提问时不抢先灌入历史，避免与发送竞态
  openChatPanel({ loadHistory: false })
  void sendMessage(question)
}

function createNewSession() {
  historyLoadToken.value += 1
  historyLoading.value = false
  abortChatStream()
  activePendingMessageId.value = ''
  const newSession = createGreetingSession()
  sessions.value = [newSession, ...sessions.value]
  activeSessionId.value = newSession.id
  openChatPanel({ loadHistory: false })
}

async function fetchAllConversationIds() {
  const ids: string[] = []
  let pageNo = 1
  const pageSize = 50
  let hasMore = true
  while (hasMore) {
    const page = await listAiConversations({ pageNo, pageSize })
    const records = page?.records || []
    records.forEach((item) => {
      if (item.conversationId) ids.push(item.conversationId)
    })
    hasMore = records.length > 0 && ids.length < (page?.total || 0)
    pageNo += 1
  }
  return ids
}

async function clearHistorySessions() {
  try {
    await ElMessageBox.confirm(
      '将清空当前会话的所有记录，并清空上下文关联',
      '确定要清空历史对话记录吗？',
      {
        customClass: 'ai-assistant-clear-confirm',
        confirmButtonText: '清空',
        cancelButtonText: '取消',
        confirmButtonClass: 'ai-assistant-clear-confirm__ok',
        cancelButtonClass: 'ai-assistant-clear-confirm__cancel',
        showClose: true,
        closeOnClickModal: true
      }
    )
    abortChatStream()
    activePendingMessageId.value = ''
    historyLoadToken.value += 1
    historyLoading.value = false
    try {
      const ids = await fetchAllConversationIds()
      await Promise.all(ids.map((conversationId) => deleteAiConversation({ conversationId })))
    } catch (error) {
      ElMessage.error((error as Error)?.message || '清空历史失败')
      return
    }
    resetToGreetingSession()
    openChatPanel({ loadHistory: false })
  } catch (error) {
    // 用户取消时不处理
  }
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  if (!target) return
  if (target.closest?.('.el-message-box, .el-overlay')) return

  const root = assistantRootRef.value
  if (!root) return
  if (root.contains(target)) return

  clearHideQuickPanelTimer()
  showQuickPanel.value = false
  showChatPanel.value = false
}

watch(
  () => route.fullPath,
  () => {
    if (!canShowAssistant.value) {
      clearHideQuickPanelTimer()
      showQuickPanel.value = false
      showChatPanel.value = false
    }
  }
)

onMounted(() => {
  resetToGreetingSession()
  loadSavedEntryBottom()
  document.addEventListener('mousedown', handleClickOutside)
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  abortChatStream()
  clearHideQuickPanelTimer()
  clearResetSuppressClickTimer()
  unbindEntryDragListeners()
  document.removeEventListener('mousedown', handleClickOutside)
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div
    v-if="canShowAssistant"
    ref="assistantRootRef"
    class="ai-assistant-root"
    :class="{ 'is-dragging': isDragging }"
    :style="{ bottom: `${entryBottom}px` }"
  >
    <div
      class="assistant-hover-zone"
      @mouseenter="handleMouseEnterEntry"
      @mouseleave="handleMouseLeaveEntry"
    >
      <div
        class="assistant-entry"
        :class="{ 'is-dragging': isDragging }"
        @pointerdown="handleEntryPointerDown"
        @click="handleOpenChatPanel"
      >
        <img :src="aiIcon" alt="AI助手" />
      </div>

      <div v-if="showQuickPanel && !showChatPanel" class="assistant-quick-panel">
        <div class="quick-header">
          <div class="quick-header-title">
            <span class="quick-ai-logo">
              <img :src="quickAiIcon" alt="AI" />
            </span>
            <span>猜你想问</span>
          </div>
          <button type="button" class="quick-close-btn" @click="closeQuickPanel">
            <img :src="quickCloseIcon" alt="关闭" />
          </button>
        </div>
        <div class="quick-list">
          <button
            v-for="question in QUICK_QUESTIONS"
            :key="question"
            type="button"
            class="quick-item"
            @click="sendFromQuickQuestion(question)"
          >
            <img :src="quickSparkleIcon" class="quick-item-icon" alt="" />
            <span class="quick-item-text">{{ question }}</span>
            <img :src="quickChevronIcon" class="quick-item-arrow" alt="" />
          </button>
        </div>
        <div class="quick-input" @click="handleOpenChatPanel">
          <span>有问题点我直接问</span>
          <img :src="sendIconDisabled" class="quick-send-icon" alt="发送" />
        </div>
      </div>
    </div>

    <div v-if="showChatPanel" ref="chatPanelRef" class="assistant-chat-panel">
      <div class="assistant-header">
        <div class="header-title">
          <img :src="headerTitleIcon" class="header-title-img" alt="AI智能助手" />
        </div>
        <button type="button" class="btn-close" @click="closeChatPanel">
          <img :src="quickCloseIcon" alt="关闭" />
        </button>
      </div>

      <div ref="scrollBodyRef" class="assistant-body">
        <div v-if="historyLoading" class="history-loading">历史记录加载中...</div>
        <div
          v-for="message in activeMessages"
          :key="message.id"
          class="message-row"
          :class="[
            `is-${message.role}`,
            message.pending && 'is-pending',
            message.kind && `kind-${message.kind}`
          ]"
        >
          <div class="message-bubble" @click="handleMessageClick">
            <span v-if="message.pending" class="pending-dot" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span class="message-text" v-html="formatAiMessageHtml(message.content)" />
          </div>
          <div
            v-if="message.id === lastReplyMessageId && activeSession?.conversationId"
            class="feedback-actions"
          >
            <button
              type="button"
              class="feedback-btn"
              :class="{ active: sessionFeedback === 'solved' }"
              :disabled="resolvingFeedback"
              @click="setSessionFeedback(AI_RESOLVE_STATUS.SOLVED)"
            >
              <img :src="sessionFeedback === 'solved' ? solvedIcon : unsolvedIcon" alt="已解决" />
              <span>已解决</span>
            </button>
            <button
              type="button"
              class="feedback-btn"
              :class="{ active: sessionFeedback === 'unsolved' }"
              :disabled="resolvingFeedback"
              @click="setSessionFeedback(AI_RESOLVE_STATUS.UNSOLVED)"
            >
              <img :src="sessionFeedback === 'unsolved' ? solvedIcon : unsolvedIcon" alt="未解决" />
              <span>未解决</span>
            </button>
          </div>
        </div>
      </div>

      <div class="assistant-footer">
        <div class="input-wrapper" :class="{ 'is-focused': inputFocused }">
          <textarea
            v-model="draft"
            class="draft-input"
            rows="2"
            placeholder="输入您的问题，如：怎么供货？"
            @focus="inputFocused = true"
            @blur="inputFocused = false"
            @keydown.enter.exact.prevent="handleSendOrStop"
          />
          <div class="tools-row">
            <div class="tool-actions">
              <ElTooltip content="新对话" placement="top">
                <button type="button" class="tool-btn" @click="createNewSession">
                  <img :src="newChatIcon" alt="新对话" />
                </button>
              </ElTooltip>
              <ElTooltip content="清除历史对话记录" placement="top">
                <button type="button" class="tool-btn" @click="clearHistorySessions">
                  <img :src="clearHistoryIcon" alt="清除历史对话记录" />
                </button>
              </ElTooltip>
            </div>
            <span class="tools-divider" />
            <button
              type="button"
              class="send-btn"
              :class="{ replying: isReplying }"
              :disabled="sendButtonDisabled"
              @click="handleSendOrStop"
            >
              <img :src="sendButtonIcon" :alt="isReplying ? '停止' : '发送'" />
            </button>
          </div>
        </div>
        <div class="footer-tip">内容由AI生成，仅供参考</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import './style.scss';
</style>
